import type { ProblemDetails } from '@quipex/shared/contracts';
import { catchError, throwError, type OperatorFunction } from 'rxjs';
import { ClientError } from './client-error';

export interface ResolvedProblemDetails extends ProblemDetails {
    /**
     * A URI reference that identifies the problem type. Defaults to "about:blank" if not present.
     */
    type?: string | null;

    /**
     * A short, human-readable summary of the problem type.
     */
    title: string;

    /**
     * The HTTP status code generated by the origin server for this occurrence of the problem.
     */
    status: number;

    /**
     * A human-readable explanation specific to this occurrence of the problem.
     */
    detail?: string | null;

    /**
     * A URI reference that identifies the specific occurrence of the problem.
     */
    instance?: string | null;

    /**
     * A dictionary of field names and error messages.
     */
    errors?: Record<string, string[]> | null;

    /**
     * Server-provided HTTP Retry-After response metadata, when available.
     */
    retryAfter?: string;

    /**
     * Extension members (any additional properties returned by the server).
     */
    [key: string]: unknown;
}

export interface ProblemToastCopy {
    message: string;
    description?: string;
}

export interface ProblemToastCopyOptions {
    /**
     * Copy used only when the server gives no useful detail or title.
     */
    defaultMessage?: string;
    /**
     * Action-specific fallbacks by HTTP status, for example delete-conflict copy
     * when a generic 409 response has no usable detail.
     */
    statusMessages?: Partial<Record<number, string>>;
    /**
     * Overrides the default two-line title/detail rendering when a caller needs
     * the detail to be the primary toast line.
     */
    linePolicy?: 'distinct-title-detail' | 'detail-first';
}

export function isProblemDetails(value: unknown): value is ProblemDetails {
    return (
        typeof value === 'object' &&
        value !== null &&
        (('title' in value && typeof value.title === 'string') ||
            ('status' in value && typeof value.status === 'number') ||
            ('type' in value && typeof value.type === 'string') ||
            ('instance' in value && typeof value.instance === 'string') ||
            ('detail' in value && typeof value.detail === 'string'))
    );
}

export function isResolvedProblemDetails(
    value: unknown
): value is ResolvedProblemDetails {
    return (
        typeof value === 'object' &&
        value !== null &&
        'title' in value &&
        typeof value.title === 'string' &&
        'status' in value &&
        typeof value.status === 'number'
    );
}

/**
 * Normalise client-side error shapes into the same ProblemDetails contract as
 * HTTP errors so toast and logging code can share one copy strategy.
 *
 * @example
 * ```ts
 * normalizeToProblemDetails(new Error('App configuration is invalid.'));
 * ```
 */
export function normalizeToProblemDetails(
    err: unknown
): ResolvedProblemDetails {
    const href = getHrefSafe();
    if (err instanceof ClientError) {
        const problemDetails: ResolvedProblemDetails = {
            status: err.status,
            title: err.statusText,
            detail: err.statusText,
            type: err.type,
            instance: err.instance ?? href,
        };

        return err.context
            ? { ...problemDetails, ...err.context }
            : problemDetails;
    }

    if (isProblemDetails(err)) {
        return {
            ...err,
            status: err.status ?? 500,
            title: err.title ?? 'Something went wrong',
            detail: err.detail,
            type: err.type,
            instance: err.instance ?? href,
        };
    }

    if (err instanceof Error) {
        return {
            status: 1000,
            title: 'Client error',
            detail: err.message || 'An unexpected client error occurred',
            instance: href,
        };
    }
    if (typeof err === 'string') {
        return {
            status: 1001,
            title: 'Client error',
            detail: err,
            instance: href,
        };
    }
    return {
        status: 1002,
        title: 'Client error',
        detail: 'Unknown error',
        instance: href,
    };
}

/**
 * Choose ngx-sonner line one and optional line two from ProblemDetails.
 * Prefer one useful line; use two lines only when title and detail are distinct.
 */
export function toProblemToastCopy(
    problemDetails: ResolvedProblemDetails,
    options: ProblemToastCopyOptions = {}
): ProblemToastCopy {
    const detail = getTrimmedStringValue(problemDetails.detail);
    const title = getTrimmedStringValue(problemDetails.title);
    const validationMessage = getValidationMessages(problemDetails.errors).join(
        '\n'
    );
    if (validationMessage) {
        if (
            title &&
            !isGenericFallbackTitle(title, problemDetails.status) &&
            !areEquivalentMessages(title, validationMessage)
        ) {
            return {
                message: title,
                description: validationMessage,
            };
        }

        return { message: validationMessage };
    }

    const statusMessage = getTrimmedStringValue(
        options.statusMessages?.[problemDetails.status]
    );
    const defaultMessage = getTrimmedStringValue(options.defaultMessage);
    const fallback =
        statusMessage ??
        defaultMessage ??
        getFallbackMessage(problemDetails.status);

    if (problemDetails.status === 0) {
        return { message: fallback };
    }

    if (title && isGenericFallbackTitle(title, problemDetails.status)) {
        const usefulDetail =
            detail &&
            (!isGenericFallbackTitle(detail, problemDetails.status) ||
                isBlanketFallbackMessage(fallback));

        return { message: usefulDetail ? detail : fallback };
    }

    if (
        options.linePolicy !== 'detail-first' &&
        title &&
        detail &&
        !areEquivalentMessages(title, detail)
    ) {
        return {
            message: title,
            description: detail,
        };
    }

    return {
        message: detail ?? title ?? fallback,
    };
}

function getHrefSafe(): string | undefined {
    try {
        return globalThis.location?.href;
    } catch {
        return undefined;
    }
}

function areEquivalentMessages(first: string, second: string): boolean {
    return (
        normalizeMessageForComparison(first) ===
        normalizeMessageForComparison(second)
    );
}

function normalizeMessageForComparison(value: string): string {
    return value.replace(/\s+/g, ' ').trim().toLocaleLowerCase();
}

function getValidationMessages(errors: unknown): string[] {
    if (!isObjectRecord(errors)) {
        return [];
    }

    return Object.values(errors).flatMap((messages) => {
        if (!Array.isArray(messages)) {
            return [];
        }

        return messages.flatMap((message) => {
            if (typeof message !== 'string') {
                return [];
            }

            const trimmedMessage = message.trim();
            return trimmedMessage ? [trimmedMessage] : [];
        });
    });
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

export function getTrimmedStringValue(value: unknown): string | undefined {
    const trimmedValue = typeof value === 'string' ? value.trim() : undefined;

    return trimmedValue || undefined;
}

export function getFallbackMessage(status: number): string {
    switch (status) {
        case 0:
            return 'Unable to reach Quipex. Check your connection and try again.';
        case 400:
            return 'Request failed. Please check your input and try again.';
        case 401:
            return 'You are not authorized to access this resource.';
        case 403:
            return 'You do not have permission to complete this action.';
        case 404:
            return 'The requested resource was not found.';
        case 409:
            return 'This action could not be completed because the item is no longer in the expected state.';
        case 422:
            return 'Request failed. Please check your input and try again.';
        case 429:
            return 'Too many requests. Please wait a moment and try again.';
        case 500:
            return 'We encountered an unexpected problem. Please try again later.';
        case 503:
            return 'The requested service is not available.';
        default:
            return 'Something went wrong!';
    }
}

function isGenericFallbackTitle(title: string, status: number): boolean {
    const normalizedTitle = normalizeMessageForComparison(title);

    return [
        'something went wrong',
        'something went wrong!',
        getReasonPhrase(status),
        getFallbackMessage(status),
    ]
        .filter(Boolean)
        .map((value) => normalizeMessageForComparison(value))
        .includes(normalizedTitle);
}

function isBlanketFallbackMessage(value: string): boolean {
    return (
        normalizeMessageForComparison(value) ===
        normalizeMessageForComparison('Something went wrong!')
    );
}

function getReasonPhrase(status: number): string {
    switch (status) {
        case 400:
            return 'Bad request';
        case 401:
            return 'Unauthorized';
        case 403:
            return 'Forbidden';
        case 404:
            return 'Not found';
        case 409:
            return 'Conflict';
        case 422:
            return 'Unprocessable entity';
        case 429:
            return 'Too many requests';
        case 500:
            return 'Internal server error';
        case 502:
            return 'Bad gateway';
        case 503:
            return 'Service unavailable';
        case 504:
            return 'Gateway timeout';
        default:
            return '';
    }
}

/**
 * RxJS helper: convert any error to ResolvedProblemDetails and rethrow.
 * Preferred usage in streams.
 *
 * @example
 * ```ts
 * import { catchAndThrowErrorAsProblemDetails } from '@quipex/shared/contracts/models/error';
 *
 * return someObservable$.pipe(
 *   catchAndThrowErrorAsProblemDetails()
 * );
 * ```
 */
export function catchAndThrowErrorAsProblemDetails<T>(): OperatorFunction<
    T,
    T
> {
    return (source) =>
        source.pipe(
            catchError((err) =>
                throwError(() => normalizeToProblemDetails(err))
            )
        );
}
